import React, { FC, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import { EDUCATIONAL_RESOURCE_TYPES } from "@/domain/educationalResources.interface";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  type: string;
  language: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const EducationalResourceFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;

  const availableLanguages = useAvailableLanguagesSetting();
  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ key: code, value: code })),
    [availableLanguages]
  );

  const typeOptions = useMemo(
    () => EDUCATIONAL_RESOURCE_TYPES.map((t) => ({ key: t, value: t })),
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.surface.light2,
          px: 2,
          width: {
            xs: "100%",
            sm: "100%",
          },
        }}
        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
      >
        <InputBase
          className="flex-1"
          placeholder="Search by title"
          inputProps={{ "aria-label": "search" }}
          onChange={(e) => {
            setFilters((filters) => ({
              ...filters,
              search: e.target.value,
              pageNumber: 0,
            }));
          }}
          value={filters.search}
        />
        <SearchIcon className="text-icon-primary" />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: {
            xs: "100%",
            sm: "100%",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
            md: "row",
          },
        }}
      >
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              status: value as string,
              pageNumber: 0,
            }));
          }}
          data={[
            { key: "ACTIVE", value: "Active" },
            { key: "INACTIVE", value: "Inactive" },
          ]}
          value={filters.status}
          label="Status"
          id="resource-status-filter"
          maxWidth={180}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              type: value as string,
              pageNumber: 0,
            }));
          }}
          data={typeOptions}
          value={filters.type}
          label="Type"
          id="resource-type-filter"
          maxWidth={180}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              language: value as string,
              pageNumber: 0,
            }));
          }}
          data={languageOptions}
          value={filters.language}
          label="Language"
          id="resource-language-filter"
          maxWidth={180}
          showClearOption
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default EducationalResourceFilters;
